import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../../shared";
import type {
  ChildFeeAccount,
  ChildFeePayment,
  ChildSchoolFees,
} from "../../../types/parentFamily";
import { formatCurrency } from "../../../utils/parentFamilyHelpers";
import {
  monthlyInstallment,
  nextPaymentNumber,
} from "../../../utils/schoolFeesHelpers";
import { nextFeeAccountPaymentNumber } from "../../../utils/feeAccountHelpers";
import {
  EVENT_DATE_PICKER_FORMAT,
  EVENT_DATE_PICKER_LOCALE,
} from "../../../utils/eventDateDisplay";
import {
  parseIsoDate,
  todayIsoDate,
  toIsoDate,
} from "../../../utils/familyDateUtils";
import { FAMILY_OUTLINED_FIELD_SX } from "./familyStyles";

export interface PaymentRecordValues {
  id?: string;
  paymentNumber: number;
  paidDate: string;
  amountPaid: string;
  receiptNo: string;
}

export type PaymentRecordLedger =
  | { kind: "school-fees"; account: ChildSchoolFees }
  | { kind: "fee-account"; account: ChildFeeAccount };

interface PaymentRecordModalProps {
  open: boolean;
  onClose: () => void;
  ledger: PaymentRecordLedger | null;
  initialPayment?: ChildFeePayment | null;
  onSave: (values: PaymentRecordValues) => void | Promise<void>;
}

export const PaymentRecordModal: React.FC<PaymentRecordModalProps> = ({
  open,
  onClose,
  ledger,
  initialPayment,
  onSave,
}) => {
  const isEdit = Boolean(initialPayment);
  const isSchoolFees = ledger?.kind === "school-fees";
  const account = ledger?.account ?? null;
  const schoolAccount = isSchoolFees ? ledger.account : null;
  const defaultInstallment = schoolAccount ? monthlyInstallment(schoolAccount) : 0;

  const [form, setForm] = useState<PaymentRecordValues>({
    paymentNumber: 1,
    paidDate: todayIsoDate(),
    amountPaid: "",
    receiptNo: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const availableSlots = useMemo(() => {
    if (!schoolAccount) return [];
    const used = new Set(
      schoolAccount.payments
        .filter((p) => p.id !== initialPayment?.id)
        .map((p) => p.paymentNumber)
    );
    const slots: number[] = [];
    for (let i = 1; i <= schoolAccount.paymentPlanMonths; i++) {
      if (!used.has(i)) slots.push(i);
    }
    return slots;
  }, [schoolAccount, initialPayment?.id]);

  useEffect(() => {
    if (!open || !account) return;
    if (initialPayment) {
      setForm({
        id: initialPayment.id,
        paymentNumber: initialPayment.paymentNumber,
        paidDate: initialPayment.paidDate.slice(0, 10),
        amountPaid: String(initialPayment.amountPaid),
        receiptNo: initialPayment.receiptNo,
      });
    } else if (isSchoolFees && schoolAccount) {
      const slot = nextPaymentNumber(schoolAccount);
      setForm({
        paymentNumber: slot <= schoolAccount.paymentPlanMonths ? slot : 1,
        paidDate: todayIsoDate(),
        amountPaid: defaultInstallment > 0 ? String(defaultInstallment) : "",
        receiptNo: "",
      });
    } else if (ledger?.kind === "fee-account") {
      setForm({
        paymentNumber: nextFeeAccountPaymentNumber(ledger.account),
        paidDate: todayIsoDate(),
        amountPaid: "",
        receiptNo: "",
      });
    }
    setError(null);
    setSaving(false);
  }, [open, account, initialPayment, defaultInstallment, isSchoolFees, ledger, schoolAccount]);

  const handleSubmit = async () => {
    if (!account) return;
    const amount = Number.parseFloat(form.amountPaid.replace(/,/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Enter a valid payment amount.");
      return;
    }
    if (!form.paidDate) {
      setError("Select the payment date.");
      return;
    }
    if (!form.receiptNo.trim()) {
      setError("Enter the receipt number.");
      return;
    }
    if (
      isSchoolFees &&
      schoolAccount &&
      (form.paymentNumber < 1 || form.paymentNumber > schoolAccount.paymentPlanMonths)
    ) {
      setError("Select a valid payment month.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save payment.");
    } finally {
      setSaving(false);
    }
  };

  if (!account) {
    return (
      <ActionModal open={open} onClose={onClose} title="Record payment">
        <Typography variant="body2" color="text.secondary">
          {isSchoolFees
            ? "School fees must be set up before recording payments."
            : "Charge not found."}
        </Typography>
      </ActionModal>
    );
  }

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit payment" : "Record payment"}
      maxWidth="sm"
      footer={
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
          <NoticeboardButton variant="secondary" size={ACTION_MODAL_FOOTER_PRIMARY_SIZE} onClick={onClose}>
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            loading={saving}
            disabled={saving}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {isEdit ? "Save payment" : "Record payment"}
          </NoticeboardButton>
        </Box>
      }
    >
      <Stack spacing={2} sx={FAMILY_OUTLINED_FIELD_SX}>
        {!isSchoolFees && ledger?.kind === "fee-account" ? (
          <Typography variant="body2" color="text.secondary">
            {ledger.account.title}
          </Typography>
        ) : null}

        {isSchoolFees && schoolAccount ? (
          <FormControl fullWidth size="small">
            <InputLabel id="payment-number-label">Payment month</InputLabel>
            <Select
              labelId="payment-number-label"
              label="Payment month"
              value={String(form.paymentNumber)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  paymentNumber: Number.parseInt(String(e.target.value), 10),
                }))
              }
            >
              {(isEdit ? [form.paymentNumber] : availableSlots).map((slot) => (
                <MenuItem key={slot} value={String(slot)}>
                  Payment {slot}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={EVENT_DATE_PICKER_LOCALE}
        >
          <DatePicker
            label="Payment date"
            format={EVENT_DATE_PICKER_FORMAT}
            value={parseIsoDate(form.paidDate)}
            onChange={(date) => setForm((f) => ({ ...f, paidDate: toIsoDate(date) }))}
            slotProps={{
              textField: { fullWidth: true, size: "small" },
            }}
          />
        </LocalizationProvider>

        <TextField
          label="Amount paid (ZAR)"
          fullWidth
          size="small"
          type="number"
          inputProps={{ min: 0, step: "0.01" }}
          value={form.amountPaid}
          onChange={(e) => setForm((f) => ({ ...f, amountPaid: e.target.value }))}
          helperText={
            isSchoolFees && schoolAccount && defaultInstallment > 0
              ? `Suggested: ${formatCurrency(defaultInstallment, schoolAccount.currency)}`
              : undefined
          }
        />

        <TextField
          label="Receipt number"
          fullWidth
          size="small"
          value={form.receiptNo}
          onChange={(e) => setForm((f) => ({ ...f, receiptNo: e.target.value }))}
          placeholder="e.g. 9294"
        />

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}
      </Stack>
    </ActionModal>
  );
};

export default PaymentRecordModal;
